/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { CloseIcon, SearchIcon } from "@/assets/icons";
import useOnClickOutside from "@/hooks/ui/useClickOutside";

import { twMerge } from "tailwind-merge";

import Divider from "./Divider";

export const TEST_ID_PREFIX = "search-autocomplete";

export interface GroupedOption<T> {
  group: string;
  items: T[];
}

interface SearchAutocompleteProps<T> {
  options?: T[];
  groupedOptions?: GroupedOption<T>[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isSearchApi?: boolean;
  noDataTxt?: string;
  noDataTxtClassName?: string;
  getOptionLabel: (option: T) => string;
  getKey: (option: T) => string | number;
  onSelect: (option: T | null) => void;
  renderOption?: (option: T, keyword: string) => JSX.Element;
  setSearchKeyword?: (value: string) => void;
  onSearch?: (clearSearchKeyword: () => void) => void;
  /** Callback function to clear the search input, typically used when the clear button is clicked. */
  onClear?: () => void;
  showSuggestionMenu?: boolean;
  /** Custom class name for suggestion items */
  suggestionItemClassName?: string;
  showNoDataAsOption?: boolean;
  testId?: string;
  error?: boolean;
  resetSearchOnSelect?: boolean;
  /** Maximum height of the suggestion dropdown in pixels */
  maxSuggestionHeight?: React.CSSProperties["maxHeight"];
}

const SearchAutocomplete = <T,>({
  options,
  groupedOptions,
  className,
  placeholder = "Search...",
  disabled = false,
  isSearchApi = false,
  noDataTxt,
  showSuggestionMenu = true,
  noDataTxtClassName,
  showNoDataAsOption = false,
  getOptionLabel,
  getKey,
  onSelect,
  renderOption,
  setSearchKeyword,
  onSearch,
  onClear,
  testId = TEST_ID_PREFIX,
  error,
  resetSearchOnSelect = true,
  suggestionItemClassName,
  maxSuggestionHeight = 240
}: SearchAutocompleteProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<T[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useOnClickOutside(ref, () => onSearch?.(() => setSearchTerm("")), []);

  // Handle filtering based on search mode and search term
  // - API mode: set all options (API handles filtering)
  // - Local mode: filter options by search term for instant feedback
  useEffect(() => {
    const allOptions = options || groupedOptions?.flatMap((g) => g.items) || [];

    let resultOptions = allOptions;

    if (!isSearchApi) {
      if (searchTerm) {
        resultOptions = allOptions.filter((option) =>
          getOptionLabel(option)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      } else {
        resultOptions = [];
      }
    }

    setFilteredOptions(resultOptions);
  }, [isSearchApi, searchTerm, options, groupedOptions, getOptionLabel]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setSearchKeyword?.(event.target.value);
    setShowSuggestions(true); // Show suggestions as the user types
    onSelect(null); // Clear the selected value in the parent component
  };

  const handleSuggestionClick = (option: T) => {
    if (resetSearchOnSelect) {
      setSearchTerm("");
      setFilteredOptions([]); // Clear suggestions after selection
    }
    setShowSuggestions(false);
    onSelect(option);
  };

  const handleBlur = () => {
    // If the input loses focus and no option is selected, you might want to reset the search term or handle it differently.
    // For example you can choose to keep the suggestions or clear them.
    setTimeout(() => setShowSuggestions(false), 200); // Small delay to allow click event to fire
  };

  const onFocusInput = () => {
    if (searchTerm?.trim()?.length && filteredOptions?.length)
      setShowSuggestions(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isSearchApi && onSearch && event.key === "Enter") {
      onSearch(() => setSearchTerm(""));
      return;
    }
    if (event.key === "Enter" && filteredOptions.length > 0) {
      handleSuggestionClick(filteredOptions[0]); // Select the first option on Enter
    }
    if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const noData = noDataTxt && !filteredOptions.length && searchTerm?.length > 0;

  return (
    <div className="w-full relative" ref={ref}>
      <div
        className={twMerge(
          "flex items-center gap-1 border-[1px] border-gainsboro rounded-[12px] px-3 h-[40px] focus-within:border-teal-500",
          disabled && "cursor-not-allowed",
          error && "!border-red-500 !focus-within:border-red-500",
          className
        )}
      >
        <SearchIcon className="w-4 h-4 fill-light-gray" />
        <input
          type="text"
          value={searchTerm}
          onFocus={onFocusInput}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="focus:outline-none w-full bg-transparent disabled:cursor-not-allowed text-xs text-txtBody"
          disabled={disabled}
          data-testid={`${testId}-search-input`}
          aria-label={TEST_ID_PREFIX}
        />
        {searchTerm && onClear && (
          <button
            type="button"
            aria-label="Clear selection"
            disabled={disabled}
            onClick={() => {
              onClear();
              setSearchTerm("");
              setShowSuggestions(false);
            }}
          >
            <CloseIcon
              className={twMerge(
                "fill-dim-gray w-5 h-5",
                disabled && "fill-light-gray"
              )}
              aria-hidden="true"
              focusable="false"
            />
          </button>
        )}
      </div>

      {showSuggestionMenu && showSuggestions && filteredOptions.length > 0 && (
        <div className="overflow-hidden absolute top-[45px] border border-gainsboro rounded-[8px] w-full bg-white z-[99]">
          <ul
            style={{ maxHeight: maxSuggestionHeight }}
            className="flex flex-col overflow-y-auto custom-scrollbar"
          >
            {groupedOptions
              ? groupedOptions.map((group, index) => {
                  const groupItems = group.items.filter((item) =>
                    filteredOptions.some(
                      (filtered) => getKey(filtered) === getKey(item)
                    )
                  );
                  if (groupItems.length === 0) return null;
                  return (
                    <li key={group.group} className="flex flex-col">
                      <div className="pl-2 py-2 text-sm font-semibold text-txtHeading">
                        {group.group}
                      </div>
                      {groupItems.map((option) => (
                        <button
                          data-testid={`${testId}-option-${getKey(option)}`}
                          key={getKey(option)}
                          tabIndex={0}
                          className={twMerge(
                            "flex items-center cursor-pointer hover:bg-teal-50 pl-2",
                            suggestionItemClassName
                          )}
                          onClick={() => handleSuggestionClick(option)}
                        >
                          {renderOption
                            ? renderOption(option, searchTerm)
                            : getOptionLabel(option)}
                        </button>
                      ))}
                      {index < groupedOptions.length - 1 && (
                        <div className="py-1">
                          <Divider className={"bg-gainsboro w-full h-[1px]"} />
                        </div>
                      )}
                    </li>
                  );
                })
              : filteredOptions.map((option) => (
                  <li
                    key={getKey(option)}
                    className={twMerge(
                      "flex items-center cursor-pointer hover:bg-teal-50 px-2",
                      suggestionItemClassName
                    )}
                    onClick={() => handleSuggestionClick(option)}
                  >
                    {renderOption
                      ? renderOption(option, searchTerm)
                      : getOptionLabel(option)}
                  </li>
                ))}
          </ul>
        </div>
      )}
      {noData && showNoDataAsOption && showSuggestions && (
        <div className="overflow-hidden absolute top-[45px] border border-gainsboro rounded-[8px] w-full bg-white z-[99]">
          <div
            className={twMerge(
              "flex h-[36px] items-center px-2 text-txtHeading",
              noDataTxtClassName
            )}
          >
            {noDataTxt}
          </div>
        </div>
      )}
      {noData && !showNoDataAsOption && (
        <div className="px-2 mt-1">
          <span
            className={twMerge("text-[10px] text-cinnabar", noDataTxtClassName)}
          >
            {noDataTxt}
          </span>
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
